import { jsx as jsx$1, ThemeContext } from '@emotion/react';
import { createContext, useContext } from 'react';
import deepmerge from 'deepmerge';
import packageInfo from '@emotion/react/package.json';
import parseProps from '@theme-ui/parse-props';

const __EMOTION_VERSION__ = packageInfo.version;
const jsx = (type, props, ...children) => jsx$1(type, parseProps(props), ...children);
/**
 * @internal for Babel JSX pragma
 * @see https://github.com/system-ui/theme-ui/issues/1603
 */

const createElement = jsx;

/**
 * @internal
 */
const __ThemeUIContext = /*#__PURE__*/createContext({
  __EMOTION_VERSION__,
  theme: {}
});
const useThemeUI = () => useContext(__ThemeUIContext);
const canUseSymbol = typeof Symbol === 'function' && Symbol.for;
const REACT_ELEMENT = canUseSymbol ? Symbol.for('react.element') : 0xeac7;
const FORWARD_REF = canUseSymbol ? Symbol.for('react.forward_ref') : 0xeac7;
const deepmergeOptions = {
  isMergeableObject: n => {
    return !!n && typeof n === 'object' && n.$$typeof !== REACT_ELEMENT && n.$$typeof !== FORWARD_REF;
  },
  arrayMerge: (_leftArray, rightArray) => rightArray
};
/**
 * Deeply merge themes
 */

const merge = (a, b) => deepmerge(a, b, deepmergeOptions);

function mergeAll(...args) {
  return deepmerge.all(args, deepmergeOptions);
}

merge.all = mergeAll;

/**
 * @internal
 */
const __ThemeUIInternalBaseThemeProvider = ({
  context,
  children
}) => jsx(ThemeContext.Provider, {
  value: context.theme
}, jsx(__ThemeUIContext.Provider, {
  value: context,
  children
}));
function ThemeProvider({
  theme,
  children
}) {
  const outer = useThemeUI();

  if (process.env.NODE_ENV !== 'production') {
    if (outer.__EMOTION_VERSION__ !== __EMOTION_VERSION__) {
      console.warn('Multiple versions of Emotion detected,', 'and theming might not work as expected.', 'Please ensure there is only one copy of @emotion/react installed in your application.');
    }
  }

  const context = typeof theme === 'function' ? { ...outer,
    theme: theme(outer.theme)
  } : merge.all({}, outer, {
    theme
  });
  return jsx(__ThemeUIInternalBaseThemeProvider, {
    context
  }, children);
}

export { ThemeProvider, __ThemeUIContext, __ThemeUIInternalBaseThemeProvider, createElement, jsx, merge, useThemeUI };
